/* eslint-disable no-param-reassign, no-unused-vars */

import Component, { ComponentLoader } from '@bythepixel/component-loader';
import { fadeOut } from '@Animations';
import Location from './location';
import Config from './config';
import Events from './events';
import { initObserver, updateUrlParams } from './utility';
import EventHandlers from './eventHandlers';

export default class LocationSearch extends Component {
  static selector = '[data-comp-location-search]';

  DEFAULT_FILTER_KEY = 'all';
  LIVEWIRE_LAZY_LOAD_DELAY = 200;

  activeLocation;
  filteredCards;
  ignoreQueryParams;
  listingContainer;
  map;
  mapResizeDelay;
  mapResizeInterval;

  locations = [];
  filteredLocations = [];
  indexedFilteredLocations = new Map();
  handlingQueryParams = false;
  filters = {};
  mapOnly = false;
  presets = [];

  constructor(container, options = {}) {
    super(container, options);

    this.mapOnly = options.mapOnly || false;

    this.attachListeners();

    if (options.lazyLoad) return;

    this.mount();
  }

  filterIsActive(filterType) {
    return this.filters[filterType]?.isSet() ?? false;
  }

  filterCount() {
    return Object.keys(this.filters).length;
  }

  allFiltersSet() {
    return Object.values(this.filters).every((filter) => filter.isActive);
  }

  uniqueVehicleTypes() {
    let departments = this.locations
      .map((location) => location.departments)
      .flat();

    if (this.filterIsActive('department')) {
      departments = departments.filter(
        (department) => department.type === this.filters.department.value,
      );
    }

    const allVehicleTypes = departments
      .map((department) => department.vehicleTypes)
      .flat();

    const unique = new Map();

    allVehicleTypes.forEach((vehicleType) => {
      if (unique.has(vehicleType)) return;

      unique.set(vehicleType, {
        value: vehicleType,
        label: vehicleType,
      });
    });

    return [...unique.values()].sort();
  }

  uniqueDepartments() {
    let departments = this.locations
      .map((location) => location.departments)
      .flat()
      .sort((deptA, deptB) => deptA.sortPriority - deptB.sortPriority);

    // if a vehicle type is specified, filter out departments that don't
    // service it
    if (this.filterIsActive('vehicleType')) {
      const selectedVehicleType = this.filters.vehicleType.value;
      departments = departments.filter((department) => department.vehicleTypes.includes(selectedVehicleType));
    }

    const unique = new Map();

    departments.forEach((department) => {
      if (unique.has(department.type)) return;

      unique.set(department.type, {
        value: department.type,
        label: department.name,
      });
    });

    return [...unique.values()];
  }

  resetFilters() {
    Object.values(this.filters).forEach((filter) => filter.reset());
  }

  hasFilteredLocations() {
    return this.filteredLocations.length > 0;
  }

  sortLocationsByDistance() {
    const sortedLocations = this.locations.sort((a, b) => {
      const aDistance = a.marker.distanceFromUser;
      const bDistance = b.marker.distanceFromUser;

      if (aDistance < bDistance) return -1;
      if (aDistance > bDistance) return 1;

      return 0;
    });

    sortedLocations.forEach((location) => this.listingContainer.appendChild(location.card));
  }

  showAllLocations() {
    this.locations.forEach((location) => {
      location.show();
      this.map.addMarker(location.marker);
    });

    this.map.refreshBounds();
  }

  deactivateLocation(options) {
    if (!this.activeLocation) return;

    this.activeLocation.deactivate(options);
    this.activeLocation = null;
  }

  updateDistances() {
    this.locations.forEach((location) => {
      location.updateDistance();
    });
  }

  updateMarkers() {
    if (!this.hasFilteredLocations()) return;

    this.filteredLocations.forEach((location) => {
      this.map.addMarker(location.marker);
    });
  }

  urlParams() {
    const filters = Object.values(this.filters);

    return filters
      .map((filter) => filter.urlParams())
      .filter((param) => param !== null);
  }

  attachListeners() {
    Object.entries(EventHandlers).forEach((handler) => {
      const [eventName, callback] = handler;

      window.addEventListener(eventName, callback.bind(this));
    });
  }

  attachMapListeners() {
    this.map.on('dragstart', () => {
      this.deactivateLocation({ keepPopupOpen: true });
    });
  }

  handleQueryParams() {
    const params = new URLSearchParams(window.location.search);

    if (params.size === 0) {
      window.dispatchEvent(Events.QueryParamsHandled);
      return;
    }

    this.handlingQueryParams = true;

    [...params.entries()].forEach((param) => {
      const [filterType, value] = param;
      const filter = this.filters[filterType];

      if (!filter) return;

      filter.set(value);
    });

    this.handlingQueryParams = false;
    this.refresh();
    window.dispatchEvent(Events.QueryParamsHandled);
  }

  presetFilters() {
    const presets = this.$container.dataset.compLocationSearchPresets;

    if (!presets) return;

    const parsedPresets = JSON.parse(presets);

    Object.entries(parsedPresets).forEach((preset) => {
      const [filterType, value] = preset;
      this.filters[filterType].set(value);
    });
  }

  updateListings() {
    return new Promise((res, rej) => {
      fadeOut(this.listingContainer, Config.listingFadeOutOptions)
        .then((animation) => {
          animation.playbackRate = 1.5;
          animation.reverse();

          if (!this.hasFilteredLocations()) {
            this.showAllLocations();
            res();
            return;
          }

          this.locations.forEach((location) => {
            const shouldHide = !this.indexedFilteredLocations.has(location.id);

            if (shouldHide) {
              location.hide();
              res();
              return;
            }

            location.show();
            this.map.addMarker(location.marker);
            res();
          });
        })
        .catch((err) => {
          console.log(err);
          rej(err);
        });
    });
  }

  indexFilteredLocations() {
    this.filteredLocations.forEach((location) => this.indexedFilteredLocations.set(location.id, true));
  }

  async refresh() {
    this.indexedFilteredLocations.clear();

    if (this.filteredLocations) {
      this.indexFilteredLocations();
    }

    await this.updateListings();
    updateUrlParams(this.urlParams());
    this.updateMarkers();
    this.map.refreshBounds();
  }

  buildFilters() {
    const { filterMapping } = Config;
    const filterEls = [...document.querySelectorAll(Config.selectors.filter)];

    filterEls.forEach((filterEl) => {
      const filterType = filterEl.dataset.compLocationSearchFilterType;
      const FilterClass = filterMapping[filterType];

      this.filters[filterType] = new FilterClass(filterEl);
    });
  }

  buildLocations() {
    const locationData = JSON.parse(this.$container.dataset.locationData);

    this.locations = locationData.map((data) => {
      const card = document.getElementById(`location_${data.id}`);
      return new Location(card, data.departments).mount();
    });
  }

  mountMap() {
    window.locationMap = new ComponentLoader(document, Config.mapComponents);
  }

  mount() {
    this.listingContainer = document.querySelector(
      Config.selectors.listingContainer,
    );
    this.ignoreQueryParams = this.$options.ignoreQueryParams || false;
    this.mapResizeInterval = this.$options.mapResizeInterval || null;

    this.buildLocations();
    this.buildFilters();
    this.mountMap();
  }

  lazyMount() {
    setTimeout(this.mount.bind(this), this.LIVEWIRE_LAZY_LOAD_DELAY);
  }
}
